import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, Loader2Icon, LockIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { BookingStep } from '../components/booking/BookingStep';
import { BookingTypeToggle } from '../components/booking/BookingTypeToggle';
import { PropertyPicker } from '../components/booking/PropertyPicker';
import { DateRangeCalendar } from '../components/booking/DateRangeCalendar';
import { GuestCounter } from '../components/booking/GuestCounter';
import { StayOptions } from '../components/booking/StayOptions';
import { PhotoshootScheduler } from '../components/booking/PhotoshootScheduler';
import { GuestDetailsForm } from '../components/booking/GuestDetailsForm';
import { AvailabilityNotice } from '../components/booking/AvailabilityNotice';
import { PriceBreakdown } from '../components/booking/PriceBreakdown';
import { BookingResult } from '../components/booking/BookingResult';
import { useBookingForm } from '../hooks/useBookingForm';
import { stayTimes } from '../data/units';
const EASE = [0.23, 1, 0.32, 1] as const;
export function BookingPage() {
  const form = useBookingForm();
  const isStay = form.bookingType === 'apartment';
  if (form.status === 'success' && form.response) {
    return <>
        <PageHeader eyebrow="Booking" title="Thank you" intro="Your request is safely with Bel Air." data-id="element-396" data-mp="2bbnw16krhb33" />
        <div className="bg-ivory-100 px-5 py-16 sm:px-8 lg:py-24" data-id="element-397" data-mp="2gba8hdsrmnau">
          <BookingResult response={form.response} onReset={form.reset} data-id="element-398" data-mp="2l9k4trq87ty1" />
        </div>
      </>;
  }
  let step = 1;
  return <>
      <PageHeader eyebrow="Booking" title="Reserve your stay" intro={`Book an apartment or a photoshoot session in a few simple steps. Check-in ${stayTimes.checkIn} · checkout ${stayTimes.checkOut}.`} data-id="element-399" data-mp="2pc8gs8a8lgz8" />

      <section className="bg-ivory-100" data-id="element-400" data-mp="uz8ujwbcpoiz">
        <div className="mx-auto max-w-[1240px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20" data-id="element-401" data-mp="zvpj2rmgq4wl">
          <form noValidate onSubmit={event => {
          event.preventDefault();
          void form.submit();
        }} className="grid gap-12 lg:grid-cols-[1.55fr_1fr] lg:gap-14" data-id="element-402" data-mp="236v6uhkhla30">
            <div className="space-y-14" data-id="element-403" data-mp="2c8mbrk77dcvt">
              <BookingStep number={step++} title="What would you like to book?" data-id="element-404" data-mp="278q7djulfjfq">
                <BookingTypeToggle value={form.bookingType} onChange={form.changeType} data-id="element-405" data-mp="27aul0tianwt7" />
              </BookingStep>

              <AnimatePresence mode="wait" initial={false} data-id="element-406" data-mp="2h6xmdx5bnrf3">
                <motion.div key={form.bookingType} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.22,
                ease: EASE
              }} className="space-y-14" data-id="element-407" data-mp="2h6eezadaslfb">
                  {isStay ? <>
                      <BookingStep number={step++} title="Choose your apartment" data-id="element-408" data-mp="2zyqii0fotsr1">
                        <PropertyPicker value={form.propertyId} onChange={form.changeProperty} data-id="element-409" data-mp="3jk8r7ii71ars" />
                      </BookingStep>
                      <BookingStep number={step++} title="Select your dates" hint="Tap your check-in date, then your check-out date." error={form.errors.dates} data-id="element-410" data-mp="3ohiotlfqksqp">
                        <DateRangeCalendar start={form.checkIn} end={form.checkOut} onChange={form.changeDates} data-id="element-411" data-mp="3ejb893pd0cp8" />
                        <AvailabilityNotice type="apartment" data-id="element-412" data-mp="30rxts46b6512" />
                      </BookingStep>
                      <BookingStep number={step++} title="Guests & requests" error={form.errors.guests} data-id="element-413" data-mp="35k7sa8lhodey">
                        <GuestCounter value={form.guests} max={form.maxGuests} onChange={form.setGuests} data-id="element-414" data-mp="2qvuvj9ekuqje" />
                        <div className="mt-8" data-id="element-415" data-mp="2vu4m16pcx2yo">
                          <StayOptions earlyCheckIn={form.earlyCheckIn} lateCheckout={form.lateCheckout} onToggleEarly={form.toggleEarly} onToggleLate={form.toggleLate} data-id="element-416" data-mp="2hu3qm6rv2nql" />
                        </div>
                      </BookingStep>
                    </> : <>
                      <BookingStep number={step++} title="Pick a date & start time" hint="Sessions run for 3 hours — the end time is set for you." error={form.errors.shoot} data-id="element-417" data-mp="250us2y116wou">
                        <PhotoshootScheduler date={form.shootDate} startTime={form.startTime} endTime={form.endTime} onDateChange={form.changeShootDate} onStartTimeChange={form.changeStartTime} data-id="element-418" data-mp="1w20y7ff34kez" />
                        <AvailabilityNotice type="photoshoot" data-id="element-419" data-mp="1h6kyatrtpuao" />
                      </BookingStep>
                      <BookingStep number={step++} title="Who’s coming?" error={form.errors.guests} data-id="element-420" data-mp="3gfi0vc1z2t3p">
                        <GuestCounter value={form.guests} max={form.maxGuests} label="People on set" hint={`Including talent and crew · maximum ${form.maxGuests}`} onChange={form.setGuests} data-id="element-421" data-mp="3bgoojmyf9854" />
                      </BookingStep>
                    </>}
                </motion.div>
              </AnimatePresence>

              <BookingStep number={step++} title="Your details" data-id="element-422" data-mp="312gzticiv5df">
                <GuestDetailsForm value={form.guest} errors={{
                fullName: form.errors.fullName,
                email: form.errors.email,
                phone: form.errors.phone
              }} onChange={form.updateGuest} data-id="element-423" data-mp="353aowf1paq7w" />

                <AnimatePresence data-id="element-424" data-mp="3ey2n9l33xh3q">
                  {form.submitError && <motion.div initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} role="alert" className="mt-8 flex items-start gap-3 rounded-sm bg-danger-50 p-4 text-[14px] leading-relaxed text-danger-700" data-id="element-425" data-mp="pav989e6uug8">
                      <AlertTriangleIcon size={17} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-426" data-mp="9l5mdjaq6ve2" />
                      <p data-id="element-427" data-mp="3i0yqeynveee8">{form.submitError}</p>
                    </motion.div>}
                </AnimatePresence>

                <motion.button type="submit" disabled={form.status === 'submitting'} whileTap={{
                scale: 0.98
              }} transition={{
                duration: 0.12
              }} className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-forest-700 px-8 py-4 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600 disabled:cursor-wait disabled:opacity-80 sm:w-auto" data-id="element-428" data-mp="3d2niith4ydyn">
                  {form.status === 'submitting' ? <>
                      <Loader2Icon size={16} className="animate-spin" aria-hidden="true" data-id="element-429" data-mp="1ejsgk4pq6s3g" />
                      Sending your request…
                    </> : 'Send booking request'}
                </motion.button>
                <p className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-muted" data-id="element-430" data-mp="14s64651nimz4">
                  <LockIcon size={13} className="mt-0.5 shrink-0 text-gold-500" aria-hidden="true" data-id="element-431" data-mp="1si0jct9g0nmj" />
                  Nothing is charged now. Your booking stays pending until payment is verified. By sending a request you agree to
                  the house rules.
                </p>
              </BookingStep>
            </div>

            <PriceBreakdown bookingType={form.bookingType} property={form.property} checkIn={form.checkIn} checkOut={form.checkOut} shootDate={form.shootDate} startTime={form.startTime} endTime={form.endTime} guests={form.guests} estimate={form.estimate} data-id="element-432" data-mp="83gq6pt5aeif" />
          </form>
        </div>
      </section>
    </>;
}